import type { PropertyValuePayload } from './property-value.js';
import { isPropertyValuePayload } from './property-value.js';
import type { ColumnConfigLike } from './select-options.js';

/** Relation column config stored in `columns[].config`. */
export type RelationColumnConfig = {
  databaseId: string;
  reciprocalColumnId?: string;
};

export function relationTargetDatabaseId(config: ColumnConfigLike): string | null {
  const raw = config?.databaseId;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/** Relation columns must not be retyped (would break links and reciprocal schema). */
export function canChangeColumnType(column: {
  type: string;
  config?: ColumnConfigLike;
}): boolean {
  if (column.type === 'relation') {
    return false;
  }
  return relationTargetDatabaseId(column.config) == null;
}

export function relationReciprocalColumnId(config: ColumnConfigLike): string | null {
  const raw = config?.reciprocalColumnId;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

export function normalizeRelationConfigForSchema(
  config: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!config) {
    return {};
  }
  const next: Record<string, unknown> = {};
  const databaseId = relationTargetDatabaseId(config);
  if (databaseId) {
    next.databaseId = databaseId;
  }
  const reciprocalColumnId = relationReciprocalColumnId(config);
  if (reciprocalColumnId) {
    next.reciprocalColumnId = reciprocalColumnId;
  }
  return next;
}

/** Extract linked block ids from a relation cell payload. */
export function parseRelationIds(
  payload: PropertyValuePayload | Record<string, unknown> | null | undefined,
): string[] {
  if (!payload || !isPropertyValuePayload(payload) || payload.t !== 'relation') {
    return [];
  }
  if (!Array.isArray(payload.v)) {
    return [];
  }
  return payload.v.map(String).filter(Boolean);
}

export function encodeRelationCellValue(ids: string[]): PropertyValuePayload | null {
  const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
  if (unique.length === 0) {
    return null;
  }
  return { t: 'relation', v: unique };
}

export type RelationIdDiff = {
  added: string[];
  removed: string[];
};

export function diffRelationIds(prev: string[], next: string[]): RelationIdDiff {
  const prevSet = new Set(prev);
  const nextSet = new Set(next);
  const added: string[] = [];
  const removed: string[] = [];
  for (const id of nextSet) {
    if (!prevSet.has(id)) {
      added.push(id);
    }
  }
  for (const id of prevSet) {
    if (!nextSet.has(id)) {
      removed.push(id);
    }
  }
  return { added, removed };
}

export function addRelationId(ids: string[], id: string): string[] {
  const trimmed = id.trim();
  if (!trimmed || ids.includes(trimmed)) {
    return ids;
  }
  return [...ids, trimmed];
}

export function removeRelationId(ids: string[], id: string): string[] {
  const trimmed = id.trim();
  if (!trimmed) {
    return ids;
  }
  return ids.filter((x) => x !== trimmed);
}
